<template>
  <div class="blog-post-page">
    <section class="main-container">
      <article class="blog-post" v-if="article" ref="article">
        <ArticleHeader :article="article" />

        <figure class="blog-post-picture" v-if="article.image">
          <img :src="headerImageUrl" class="img-responsive" />
          <figcaption
            class="text-right"
            v-if="article.image_description"
            v-html="article.image_description"
          ></figcaption>
        </figure>

        <div class="blog-post__content">
          <v-runtime-template
            :template="
              `<div><div id='toc' v-if='article.show_table_of_contents && article.table_of_contents'>${article.table_of_contents}</div>${article.content}</div>`
            "
          />
        </div>
        <div class="blog-post__footer-wrapper">
          <div class="separator-big"></div>
          <ArticleFooter :article="article" @srtollTop="srtollTop" />
        </div>
      </article>
    </section>
  </div>
</template>

<script>
import { DEV_PROFILES_PAGE_NAME } from "../router/pageNameConstants";
import { parseDate } from "../lib/utils";

import ArticleHeader from "../components/common/article/Header.vue";
import ArticleFooter from "../components/common/article/Footer.vue";

import baguetteBox from "baguettebox.js";

import { ArticleResource } from "@/services";
import { mapGetters } from "vuex";

import VRuntimeTemplate from "v-runtime-template";

import AppPicture from "@/components/common/article/widgets/AppPicture";
import AppGallery from "@/components/common/article/widgets/AppGallery";
import AppGallerySlide from "@/components/common/article/widgets/AppGallerySlide";
import AppYoutube from "@/components/common/article/widgets/AppYoutube";
import AppTweet from "@/components/common/article/widgets/AppTweet";
import AppFacebook from "@/components/common/article/widgets/AppFacebook";
import AppTwitch from "@/components/common/article/widgets/AppTwitch";
import AppInstagram from "@/components/common/article/widgets/AppInstagram";
import AppImgur from "@/components/common/article/widgets/AppImgur";
import AppGiphy from "@/components/common/article/widgets/AppGiphy";
import AppVimeo from "@/components/common/article/widgets/AppVimeo";
import AppSoundcloud from "@/components/common/article/widgets/AppSoundcloud";
import AppBackToContents from "@/components/common/article/widgets/AppBackToContents";
import AppAuthor from "@/components/common/article/widgets/AppAuthor";
import AppComparison from "bi-vue-image-comparison";

import EventBus from "@/EventBus";

export default {
  components: {
    ArticleHeader,
    ArticleFooter,
    AppPicture,
    AppGallery,
    AppGallerySlide,
    AppYoutube,
    AppTweet,
    AppComparison,
    AppFacebook,
    AppTwitch,
    AppInstagram,
    AppImgur,
    AppGiphy,
    AppVimeo,
    AppSoundcloud,
    AppAuthor,
    AppBackToContents,
    VRuntimeTemplate
  },
  data() {
    return {
      article: null,
      galleryInitialised: false,
      DEV_PROFILES_PAGE_NAME
    };
  },
  metaInfo() {
    return {
      title: this.title,
      meta: [
        {
          vmid: "og:image",
          property: "og:image",
          content: this.article && this.article.preview_image_url
        },
        {
          vmid: "description",
          name: "description",
          content: this.article && this.article.intro_text
        },
        {
          vmid: "og:description",
          property: "og:description",
          content: this.article && this.article.intro_text
        },
        { vmid: "og:type", property: "og:type", content: "article" }
        // TODO: http://ogp.me/#type_article - type article also has some extra tags, which we can use for SEO optimization
      ]
    };
  },
  computed: {
    ...mapGetters("settings", {
      baseAssetsPath: "baseAssetsPath"
    }),
    headerImageUrl() {
      return `${this.baseAssetsPath}article/${this.article.id}/${this.article.image}`;
    },
    title() {
      return this.article && this.article.title;
    }
  },
  methods: {
    parseDate,
    srtollTop() {
      const article = this.$refs.article;
      this.$scrollTo(article);
    },
    async loadData() {
      let response;

      if (this.$route.query.token) {
        response = await ArticleResource.preview({
          token: this.$route.query.token
        });
      } else {
        response = await ArticleResource.get({
          id: this.$route.params.slug
        });
      }

      this.$store.commit("settings/setLoading", true);
      this.article = response.data;

      this.$nextTick(function() {
        this.$store.dispatch("settings/setLoading", false);
        setTimeout(() => {
          baguetteBox.run(".image-gallery");
          EventBus.$emit("dataLoaded");
        }, 100);
        this.galleryInitialised = true;
      });
    }
  },
  created() {
    this.loadData();
  },
  destroyed() {
    if (this.galleryInitialised) {
      baguetteBox.destroy();
    }
  }
};
</script>

<style lang="scss">
@import "./../assets/styles/vars";

$h-padding: 100px;
$v-padding: 40px;
$screen-big: 1270px;

.blog-post__footer-wrapper {
  max-width: 939px;
  margin: 0 auto;
}

.blog-post-page {
  padding-top: 60px;
  padding-bottom: 50px;

  @media (max-width: $screen-xl) {
    padding-top: 30px;
  }
}

.blog-post-picture {
  margin-bottom: 50px;

  figcaption {
    margin-top: 15px;
    font-family: Metron Book, sans-serif;
    font-size: 13px;
    color: #9b9b9b;
  }
}

.blog-post {
  max-width: 1140px;
  margin: 0 auto;

  @media (max-width: $screen-md) {
    padding-left: 30px;
    padding-right: 30px;
  }

  @media (max-width: $screen-xs) {
    padding-left: 13px;
    padding-right: 13px;
  }

  p {
    margin: $v-padding 0;
  }
}

////////////////////////////////////////////////////
///              CONTENT                        ///
//////////////////////////////////////////////////

.blog-post__content {
  a[href] {
    color: $mainRed;

    &:hover {
      color: darken($mainRed, 12%);
    }
  }

  a:not([href]) {
    &:before {
      content: "";
      display: block;
      position: relative;
      width: 0;
      height: 5em;
      margin-top: -5em;
    }
  }

  #toc {
    &:before {
      content: "";
      display: block;
      position: relative;
      width: 0;
      height: 5em;
      margin-top: -5em;
    }
  }

  max-width: 939px;
  margin: 0 auto;

  h2 {
    padding-bottom: 23px;
    font-family: $sarala;
    font-size: 20px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    border-bottom: 1px solid #333;

    @media (max-width: $screen-big) {
      margin-left: 0;
      margin-right: 0;
    }

    i {
      margin-left: 10px;
      color: #4d4d4d;
      font-size: 16px;
    }

    &:hover {
      a {
        display: inline-block;
      }
    }
  }

  h3,
  h4,
  h5,
  h6 {
    font-family: $sarala;
    line-height: 1.2;
  }

  & > h3,
  & > h4,
  & > h5,
  & > h6 {
    margin: 0 0 $v-padding;

    @media (max-width: $screen-big) {
      margin-left: 0;
      margin-right: 0;
    }
  }

  h3 {
    font-size: 20px;
  }

  h4 {
    font-size: 18px;
  }

  h5 {
    font-size: 16px;
  }

  h6 {
    font-size: 14px;
  }

  p {
    font-family: $metron;
    font-size: 18px;
    line-height: 1.95;

    @media (max-width: $screen-big) {
      margin-left: 0;
      margin-right: 0;
    }

    a {
      color: $mainRed;
      border-bottom: 1px solid $mainRed;
    }
  }

  ul,
  ol {
    margin: $v-padding 0 $v-padding;
    padding: 0 0 0 54px;

    @media (max-width: $screen-big) {
      margin-left: 0;
      margin-right: 0;
    }

    @media (max-width: $screen-sm) {
      padding-left: 20px;
    }

    ul,
    ol {
      margin: 1em 0 2em;
      padding-left: 2em;

      li {
        padding-left: 10px;
      }
    }

    li {
      padding-left: 18px;
      font-family: $metron;
      font-size: 18px;
      line-height: 1.3;
      margin-bottom: 12px;

      @media (max-width: $screen-sm) {
        padding-left: 5px;
      }
    }
  }

  blockquote {
    position: relative;
    padding: 0;
    margin: 55px 50px 65px 100px;
    font-family: $sarala;
    font-weight: 400;
    font-size: 28px;
    font-style: normal;
    color: #b3b3b3;
    line-height: 1.5;

    &::before {
      content: "";
      position: absolute;
      display: block;
      width: 5px;
      top: 0;
      bottom: 0;
      left: -30px;
      background-color: $mainRed;
    }

    @media (max-width: $screen-big) {
      margin-left: 50px;
      margin-right: 0;
    }

    @media (max-width: $screen-sm) {
      margin-left: 20px;
      font-size: 22px;

      &::before {
        left: -20px;
      }
    }

    @media (max-width: $screen-xs) {
      margin: 40px 0 40px 10px;
      font-size: 20px;
    }
  }

  strong {
    font-family: $sarala;
  }
}
</style>
